import { EmitterConfigV2 } from "@pixi/particle-emitter";
import { gsap } from "gsap";
import { IDestroyOptions, Point, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { GameFont } from "../../../common/GameFont";
import { RewardContainer } from "../../../common/components/RewardContainer";
import { LevelUpParticleAnimationView } from "../../../common/components/levelup/ParticleAnimationView";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { BonusPercentContainer } from "../../../common/components/BonusPercentContainer";
import { IUserLevelUpController } from "../controller/IUserLevelUpController";
import { IUserLevelUpModel } from "../model/IUserLevelUpModel";
import { UserLevelUpResourcePackage } from "../resources/UserLevelUpResourcePackage";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { DefaultButtonWithText } from "../../../common/components/DefaultButtonWithText";
import { Avatar } from "../../../common/components/Avatar";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { CrownsResourcePackage } from "../../../common/resources/CrownsResourcePackage";

export class UserLevelUpPopupView extends BaseScalableView<void> {
    private static readonly COLLECT_TEXT = "COLLECT";
    private static readonly COIN_REWARD_BACKGROUND_SCALE = 2.2;

    private readonly commonResources: CommonResourcesPackage;
    private readonly userLevelUpResources: UserLevelUpResourcePackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly crownsResources: CrownsResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly userLevelUpController: IUserLevelUpController;
    private readonly userLevelUpModel: IUserLevelUpModel;
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;

    private crownsBackGround: Sprite;
    private fadeScreen: FadeScreen;
    private collectButton: DefaultButtonWithText;
    private coinsRewardContainer: RewardContainer;
    private wheelPercentRewardContainer: BonusPercentContainer;
    private otherPercentRewardContainer: BonusPercentContainer;
    private levelUpSprite: Sprite;
    private levelText: Text;
    private avatar: Avatar;
    private avatarTargetPosition: Point;
    private firstRewardLight: Sprite;
    private secondRewardLight: Sprite;
    private crownsFlyAnimation: LevelUpParticleAnimationView;

    public constructor(
        commonResources: CommonResourcesPackage,
        userLevelUpResources: UserLevelUpResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        crownsResources: CrownsResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        canvasScaler: ICanvasScaler,
        userLevelUpController: IUserLevelUpController,
        userLevelUpModel: IUserLevelUpModel,
        guestModel: IGuestModel,
        playerModel: IPlayerModel,
        operationControls: PromiseCompletionSource<void>
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.userLevelUpResources = userLevelUpResources;
        this.userProfileResources = userProfileResources;
        this.crownsResources = crownsResources;
        this.soundResources = soundResources;
        this.userLevelUpController = userLevelUpController;
        this.userLevelUpModel = userLevelUpModel;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.show.bind(this));
    }

    public start(): void { }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.5, 0, 0.9, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);
        this.crownsBackGround = new Sprite(this.userLevelUpResources.assets_views_user_level_up_ui_crowns_crowns_bg_png);
        this.crownsBackGround.anchor.set(0.5);
        this.addChild(this.crownsBackGround);
        this.levelUpSprite = new Sprite(this.userLevelUpResources.assets_views_user_level_up_ui_tittle_lvl_up_tittle_png);
        this.levelUpSprite.anchor.set(0.5);
        this.addChild(this.levelUpSprite);
        this.avatar = new Avatar(this.userProfileResources, this.guestModel.avatarTexture.value);
        this.avatar.enabled = false;
        this.avatar.avatarSize = new Point(220, 220);
        this.avatar.trophySize = new Point(117, 150);
        this.avatar.glow = this.playerModel.isAuthorized;
        this.addChild(this.avatar);
        this.levelText = new Text(`Lv.${this.userLevelUpModel.playerDto.level}`, new LevelTextStyle());
        this.levelText.anchor.set(0.5);
        this.addChild(this.levelText);
        this.collectButton = new DefaultButtonWithText(this.commonResources.assets_common_ui_button_green_png, UserLevelUpPopupView.COLLECT_TEXT, new CollectTextStyle());
        this.collectButton.pivot.set(this.collectButton.width * 0.5, this.collectButton.height * 0.5);
        this.collectButton.onUp.add(this.collectClickHandle, this);
        this.addChild(this.collectButton);
        this.firstRewardLight = new Sprite(this.userLevelUpResources.assets_views_user_level_up_ui_light_violet_light_png);
        this.firstRewardLight.anchor.set(0.5);
        this.addChild(this.firstRewardLight);
        this.secondRewardLight = new Sprite(this.userLevelUpResources.assets_views_user_level_up_ui_light_violet_light_png);
        this.secondRewardLight.anchor.set(0.5);
        this.addChild(this.secondRewardLight);
        this.coinsRewardContainer = new RewardContainer(this.commonResources.assets_common_ui_top_panel_coin_png, this.userLevelUpModel.rewardInCoins);
        this.addChild(this.coinsRewardContainer);
        this.wheelPercentRewardContainer = new BonusPercentContainer(this.userLevelUpResources.assets_views_user_level_up_ui_reward_icons_wheel_icon_png, this.userLevelUpModel.additionalWheelRewardPercentage);
        this.addChild(this.wheelPercentRewardContainer);
        this.otherPercentRewardContainer = new BonusPercentContainer(this.userLevelUpResources.assets_views_user_level_up_ui_reward_icons_target_png,
            this.userLevelUpModel.additionalRaidStealRewardPercentage
        );
        this.addChild(this.otherPercentRewardContainer);
        this.buildCrownsParticle();
    }

    private buildCrownsParticle(): void {
        const textures = [
            this.crownsResources.assets_views_crowns_s7_png,
            this.crownsResources.assets_views_crowns_s2_png,
            this.crownsResources.assets_views_crowns_s3_png,
            this.crownsResources.assets_views_crowns_s4_png,
            this.crownsResources.assets_views_crowns_s6_png,
            this.crownsResources.assets_views_crowns_s10_png,
            this.crownsResources.assets_views_crowns_s11_png
        ];
        const starsFlyConfig = this.userLevelUpResources.assets_views_user_level_up_emmiters_crowns_fly_json as EmitterConfigV2;
        this.crownsFlyAnimation = new LevelUpParticleAnimationView(starsFlyConfig, textures);
        this.addChild(this.crownsFlyAnimation);
    }

    private async show(): Promise<void> {
        this.crownsFlyAnimation.play();
        this.fadeScreen.fadeIn();
        this.soundResources.assets_sounds_Collect_Screen_mp3.play();
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.set(this.collectButton, { eventMode: "none" });
        animationTimeLine.to(this.avatar, { x: this.avatarTargetPosition.x, y: this.avatarTargetPosition.y, duration: 0.6, ease: "power1.in" });
        animationTimeLine.fromTo(this.crownsBackGround, { y: this.canvasScaler.getSize().height * 1.5, width: 0, height: 0 },
            {
                y: this.crownsBackGround.y,
                width: this.crownsBackGround.width,
                height: this.crownsBackGround.height,
                duration: 0.5,
                ease: "power1.out"
            }, "<50%");
        animationTimeLine.fromTo(this.levelUpSprite.scale, { x: 0, y: 0 }, { x: this.levelUpSprite.scale.x, y: this.levelUpSprite.scale.y, duration: 0.5, ease: "back.out(2)" }, "<90%");
        animationTimeLine.fromTo(this.collectButton.scale, { x: 0, y: 0 }, { x: this.collectButton.scale.x, y: this.collectButton.scale.y, duration: 0.5, ease: "back.out(2)" }, "<");
        animationTimeLine.fromTo(this.firstRewardLight.scale, { x: 0, y: 0 }, { x: this.firstRewardLight.scale.x, y: this.firstRewardLight.scale.y, duration: 0.3, ease: "back.out(2)" }, "<");
        animationTimeLine.fromTo(this.secondRewardLight.scale, { x: 0, y: 0 }, { x: this.secondRewardLight.scale.x, y: this.secondRewardLight.scale.y, duration: 0.3, ease: "back.out(2)" }, "<");
        animationTimeLine.fromTo(this.levelText.scale, { x: 0, y: 0 }, { x: this.levelText.scale.x, y: this.levelText.scale.y, duration: 0.3, ease: "back.out(2)" }, "<50%");
        animationTimeLine.fromTo(this.otherPercentRewardContainer.scale, { x: 0, y: 0 }, { x: this.otherPercentRewardContainer.scale.x, y: this.otherPercentRewardContainer.scale.y, duration: 0.5, ease: "elastic.out(1, 0.4)" }, "<90%");
        animationTimeLine.fromTo(this.wheelPercentRewardContainer.scale, { x: 0, y: 0 }, { x: this.wheelPercentRewardContainer.scale.x, y: this.wheelPercentRewardContainer.scale.y, duration: 0.5, ease: "elastic.out(1, 0.4)" }, "<30%");
        animationTimeLine.fromTo(this.coinsRewardContainer.scale, { x: 0, y: 0 }, { x: this.coinsRewardContainer.scale.x, y: this.coinsRewardContainer.scale.y, duration: 0.5, ease: "elastic.out(1, 0.4)" }, "<30%");
        animationTimeLine.set(this.collectButton, { eventMode: "dynamic" });
        animationTimeLine.then(() => animationTimeLine.kill());
    }

    private async hide(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.crownsBackGround, { y: 0, width: 0, height: 0, duration: 0.3, ease: "power2.in" });
        animationTimeLine.to(this.collectButton.scale, { x: 0, y: 0, duration: 0.3, ease: "back.in(2)" }, "<5%");
        animationTimeLine.to(this.coinsRewardContainer.scale, { x: 0, y: 0, duration: 0.3, ease: "back.in(2)" }, "<20%");
        animationTimeLine.to(this.firstRewardLight.scale, { x: 0, y: 0, duration: 0.3, ease: "back.in(2)" }, "<");
        animationTimeLine.to(this.secondRewardLight.scale, { x: 0, y: 0, duration: 0.3, ease: "back.in(2)" }, "<");
        animationTimeLine.to(this.levelText.scale, { x: 0, y: 0, duration: 0.3, ease: "back.in(2)" }, "<");
        animationTimeLine.to(this.wheelPercentRewardContainer.scale, { x: 0, y: 0, duration: 0.3, ease: "back.in(2)" }, "<");
        animationTimeLine.to(this.otherPercentRewardContainer.scale, { x: 0, y: 0, duration: 0.3, ease: "back.in(2)" }, "<");
        animationTimeLine.to(this.levelUpSprite.scale, { x: 0, y: 0, duration: 0.4, ease: "back.in(3)" }, "<20%");
        animationTimeLine.to(this.avatar, {
            x: this.userLevelUpModel.avatarPosition.x,
            y: this.userLevelUpModel.avatarPosition.y,
            duration: 0.6,
            ease: "power1.out"
        });
        animationTimeLine.add(() => this.fadeScreen.fadeOut(), "<");
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private async collectClickHandle(): Promise<void> {
        this.collectButton.eventMode = "none";
        this.soundResources.assets_sounds_Choose_Card_mp3.play();
        this.userLevelUpController.collectCoins(this.userLevelUpModel.rewardInCoins);
        await this.hide();
        this.operationControls.complete();
    }

    protected layout(width: number, height: number): void {
        this.crownsBackGround.position.set(421, 551.2);
        this.levelUpSprite.position.set(width * 0.5, 408.7);
        this.levelText.position.set(width * 0.5, 899.1);
        this.avatar.position.set(this.userLevelUpModel.avatarPosition.x, this.userLevelUpModel.avatarPosition.y);
        this.avatarTargetPosition = new Point(width * 0.5 - this.avatar.width * 0.5, 620.2 - this.avatar.height * 0.5);
        this.firstRewardLight.position.set(width * 0.5, 1109.2);
        this.coinsRewardContainer.scale.set(UserLevelUpPopupView.COIN_REWARD_BACKGROUND_SCALE);
        this.coinsRewardContainer.position.set(width * 0.5, 1325.93);
        this.wheelPercentRewardContainer.position.set(width * 0.5, 1177.7 - this.wheelPercentRewardContainer.height * 0.5);
        this.secondRewardLight.position.set(width * 0.5, 1258.2);
        this.otherPercentRewardContainer.position.set(width * 0.5, 1033.5 - this.otherPercentRewardContainer.height * 0.5);
        this.collectButton.position.set(width * 0.5, 1516.12);
        this.crownsFlyAnimation.position.set(width * 0.5, height * 1.5);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.collectButton.onUp.remove(this.collectClickHandle, this);
        this.crownsFlyAnimation.destroy();
        super.destroy(options);
    }
}

class CollectTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#e8e8e8",
            fontFamily: GameFont.DEFAULT_FONT,
            dropShadow: true,
            dropShadowAlpha: 0.5,
            dropShadowAngle: 0.5,
            dropShadowBlur: 5,
            dropShadowDistance: 4,
            fontSize: 78,
            leading: 15
        }));
    }
}

class LevelTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 188,
            dropShadow: true,
            dropShadowAngle: 0,
            dropShadowAlpha: 1,
            dropShadowBlur: 20,
            dropShadowColor: "#c5a3e1",
            dropShadowDistance: 5,
            padding: 50,
            stroke: "#c5a3e1",
            strokeThickness: 5
        }));
    }
}
